import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/services/store";
import type { RootState } from "@/services/store";
import type { BlockingModalOptions } from "./types";
import { blockingModalActions } from "./blocking-modal-slice";

export const useBlockingModal = (
  isLoadingSelector: (state: RootState) => boolean,
  options?: BlockingModalOptions,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { title = t("common.loading"), content = t("common.pleaseWait") } = options ?? {};

  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);

  const isLoading = useAppSelector(isLoadingSelector);

  useEffect(() => {
    if (isLoading && pendingCallback) {
      dispatch(blockingModalActions.setBlockingModalOptions({ title, content }));
      dispatch(blockingModalActions.setLoadingIsVisible(true));
    }
  }, [isLoading, pendingCallback]);

  useEffect(() => {
    if (!isLoading && pendingCallback) {
      pendingCallback();
      setPendingCallback(null);
      dispatch(blockingModalActions.setLoadingIsVisible(false));
    }
  }, [isLoading]);

  const executeWithModal = (callback: () => void) => {
    if (isLoading) {
      setPendingCallback(() => callback);
    } else {
      callback();
    }
  };

  return {
    executeWithModal,
  };
};
